// Provenance: repo:tds-reconciliation-portal:frontend/src/shell/ErrorBoundary.tsx (ADR-0005)
// — the fallback restyled on the estate tokens (ADR-0010); `errorText` and the boundary's
// claims carry byte-for-thought.
//
// The screen that appears instead of a white page. Found by crashing the real
// app during a visual pass: one component throwing took the ENTIRE page —
// sidebar, header, everything. `npm run build` and vitest were both green at
// the time; only running it showed anything.
//
// WHAT THIS IS ALLOWED TO CLAIM
//
// A render failure is a DISPLAY failure. It does not record, undo or complete
// anything by itself. But it must not overclaim the other way either: if the
// crash landed just after a button was pressed, the REQUEST may well have
// succeeded and only the re-render died — so the honest instruction is to
// check before pressing again, not "nothing happened". Auto-match takes crores
// in one press; "just try again" is not advice this product can afford.

import { AlertTriangle } from 'lucide-react'
import { Component, useState } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

const MAX = 500

/** An unknown thrown value as one line worth showing and copying.
 *
 *  React hands a boundary `unknown`, and `err.message` is right only for an
 *  Error. Everything else a `throw` can carry — a string, a number, null, an
 *  object with a hostile getter — has to come out as something, because
 *  "undefined" on a crash screen is worse than no detail at all.
 */
export function errorText(err: unknown): string {
  let out: string
  try {
    if (err instanceof Error) {
      out = `${err.name}: ${err.message}`
    } else if (typeof err === 'string' && err.trim()) {
      out = err
    } else if (err === undefined || err === null) {
      // NOT String(err): "undefined" on a crash screen reads as a second bug
      out = 'An empty value was thrown'
    } else if (typeof err === 'object') {
      // the constructor name, not Object.prototype.toString — "[object Object]"
      // tells the reader nothing they did not already know
      const kind = err.constructor?.name || 'object'
      out = `Non-Error thrown (${kind}): ${JSON.stringify(err)}`
    } else {
      out = `Non-Error thrown (${typeof err}): ${String(err)}`
    }
  } catch {
    // reporting the throw must not itself throw, or the original is lost
    out = 'Unreadable error value'
  }
  if (!out.trim()) out = 'Unreadable error value'
  return out.length > MAX ? out.slice(0, MAX - 1) + '…' : out
}

function Fallback({ err, onRetry }: { err: unknown; onRetry: () => void }) {
  const [show, setShow] = useState(false)
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-destructive">
        <AlertTriangle size={20} />
        <h2 className="text-base font-semibold">This screen could not be drawn</h2>
      </div>
      <div className="space-y-2 text-sm text-foreground">
        <p>
          Something in the page failed while rendering. This is a <b>display</b>{' '}
          failure: on its own it does not record, undo or complete anything, and
          no figure has been changed by it.
        </p>
        <p>
          If you had just pressed something, <b>check whether it took effect
          before pressing it again</b> — the request may have succeeded and only
          the redraw failed. Reloading is safe; repeating an action may not be.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
            onClick={onRetry}
          >
            Try this screen again
          </button>
          <button
            className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted"
            onClick={() => window.location.reload()}
          >
            Reload the page
          </button>
          <button
            className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:bg-muted"
            onClick={() => setShow((s) => !s)}
          >
            {show ? 'Hide' : 'Show'} the technical detail
          </button>
        </div>
        {show && (
          /* worth copying into a bug report verbatim; the console keeps the
             stack, which is why this is a line rather than a dump */
          <pre className="overflow-x-auto rounded bg-muted p-2 text-xs">{errorText(err)}</pre>
        )}
      </div>
    </div>
  )
}

interface Props {
  children: ReactNode
  /** changing this clears a caught error — the route path, so navigating away
   *  from a broken screen is enough to leave it behind */
  resetKey?: string
}

export class ErrorBoundary extends Component<Props, { err: unknown; caught: boolean }> {
  state = { err: null as unknown, caught: false }

  static getDerivedStateFromError(err: unknown) {
    return { err, caught: true }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.caught && prev.resetKey !== this.props.resetKey) {
      this.setState({ err: null, caught: false })
    }
  }

  componentDidCatch(err: unknown, info: ErrorInfo) {
    // NOT swallowed. The console keeps the stack and the component trace, which
    // is what actually diagnoses this — the screen only carries the one line.
    console.error('Render failed:', err, info.componentStack)
  }

  render() {
    if (!this.state.caught) return this.props.children
    return (
      <Fallback err={this.state.err}
                onRetry={() => this.setState({ err: null, caught: false })} />
    )
  }
}
